var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { generateUUID } from './uuid';
import { trackGoogleAnalyticsPageView } from './googleAnalytics';
// Lightweight client-side debounce to prevent duplicate bursts
var LAST_SENT = {};
var DEBOUNCE_MS = 3000; // 3s window per (type|route|source)
var ANALYTICS_SESSION_STORAGE_KEY = 'analytics_session_id';
function safeTrim(value) {
    return typeof value === 'string' ? value.trim() : '';
}
function getCurrentRoute() {
    var _a;
    if (typeof window === 'undefined') {
        return '/';
    }
    return ((_a = window.location) === null || _a === void 0 ? void 0 : _a.pathname) || '/';
}
function getTrafficMetadata() {
    if (typeof window === 'undefined') {
        return {};
    }
    try {
        var params = new URLSearchParams(window.location.search);
        var referrer = safeTrim(document === null || document === void 0 ? void 0 : document.referrer);
        var referrerHost = void 0;
        if (referrer) {
            try {
                referrerHost = new URL(referrer).hostname || undefined;
            }
            catch (_error) {
                // Ignore invalid referrer values
            }
        }
        var utmSource = safeTrim(params.get('utm_source')) || undefined;
        var utmMedium = safeTrim(params.get('utm_medium')) || undefined;
        var utmCampaign = safeTrim(params.get('utm_campaign')) || undefined;
        var utmContent = safeTrim(params.get('utm_content')) || undefined;
        var utmTerm = safeTrim(params.get('utm_term')) || undefined;
        var pagePath = "".concat(window.location.pathname || '/').concat(window.location.search || '');
        var viewport = "".concat(window.innerWidth || 0, "x").concat(window.innerHeight || 0);
        var timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
        return {
            referrerHost: referrerHost,
            utmSource: utmSource,
            utmMedium: utmMedium,
            utmCampaign: utmCampaign,
            utmContent: utmContent,
            utmTerm: utmTerm,
            pagePath: pagePath,
            viewport: viewport,
            timeZone: timeZone,
        };
    }
    catch (_error) {
        return {};
    }
}
export function getAnalyticsSessionId() {
    if (typeof window === 'undefined') {
        return 'server';
    }
    try {
        var existing = window.sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);
        if (existing && existing.trim()) {
            return existing;
        }
        var created = generateUUID();
        window.sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, created);
        return created;
    }
    catch (_error) {
        // Storage can fail in private mode; still include a runtime id for this call.
        return generateUUID();
    }
}
function normalizeMetadata(_type, metadata) {
    var route = getCurrentRoute();
    var source = safeTrim(metadata === null || metadata === void 0 ? void 0 : metadata.source) || 'unknown';
    var trafficMetadata = getTrafficMetadata();
    return __assign(__assign(__assign({}, trafficMetadata), metadata), { route: route, source: source, analyticsSessionId: getAnalyticsSessionId() });
}
export var trackEvent = function (type, metadata) {
    // Block only if REACT_APP_DISABLE_ANALYTICS is 'true'
    // With REACT_APP_DISABLE_ANALYTICS=false, analytics works in all environments including dev
    if (process.env.REACT_APP_DISABLE_ANALYTICS === 'true') {
        return;
    }
    try {
        var enriched = normalizeMetadata(type, metadata);
        var key = "".concat(type, "|").concat(enriched.route, "|").concat(enriched.source);
        var now = Date.now();
        var last = LAST_SENT[key] || 0;
        if (now - last < DEBOUNCE_MS)
            return; // drop duplicate burst
        LAST_SENT[key] = now;
        var body = JSON.stringify({ type: type, metadata: enriched });
        var url = '/api/analytics';
        if (navigator.sendBeacon) {
            var blob = new Blob([body], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        }
        else {
            fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true }).catch(function () { });
        }
    }
    catch (_) { }
};
export var trackPageView = function (metadata) {
    var path = typeof window !== 'undefined'
        ? "".concat(window.location.pathname || '/').concat(window.location.search || '')
        : '/';
    var pageTitle = typeof document !== 'undefined' ? document.title : undefined;
    trackGoogleAnalyticsPageView(path, pageTitle);
    trackEvent('page_view', __assign({ source: 'auto_route', path: path, pageTitle: pageTitle }, metadata));
};
export var trackWebVital = function (metric) {
    if (!metric || typeof metric.name !== 'string') {
        return;
    }
    trackEvent('web_vital', {
        source: 'web_vitals',
        metricName: metric.name,
        metricId: metric.id,
        metricValue: typeof metric.value === 'number' ? metric.value : Number(metric.value),
        metricDelta: typeof metric.delta === 'number' ? metric.delta : undefined,
        metricRating: metric.rating,
        navigationType: metric.navigationType,
    });
};
